import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchHarvestDetailDropdownData,
  fetchHarvestFieldsByLandlords,
  filterHarvestDetailData,
  putHarvestDetailApi,
} from "../services/harvestdetail.api";
import { toast } from "react-toastify";

interface CellUpdate {
  harvestDetailId: number;
  fieldName: string;
  value: string;
}

interface UpdateHarvestDetailRequest {
  cellUpdates: CellUpdate[];
}

interface GetHarvestDetailsByYearAndLandlordsRequest {
  year: number;
  landlordIds: number[];
  cropIds?: number[];
  fieldIds?: number[];
}

interface EditableColumnMetadata {
  columnIndex: number;
  fieldName: string;
  displayName: string;
  dataType: string;
  minValue?: number;
  maxValue?: number;
  isRequired?: boolean;
  validationMessage?: string;
}

interface UpdatedCellResponse {
  harvestDetailId: number;
  fieldName: string;
  newValue: any;
  success: boolean;
  errorMessage?: string;
}

interface CalculatedFieldResponse {
  harvestDetailId: number;
  fieldName: string;
  newValue: any;
  success: boolean;
  errorMessage?: string;
}

interface UpdateHarvestDetailResponse {
  success: boolean;
  updatedCells: UpdatedCellResponse[];
  calculatedFields: CalculatedFieldResponse[];
  errors: string[];
}

interface DropdownItem {
  id: number;
  name: string;
}

interface HarvestDetailState {
  availableYears: DropdownItem[];
  availableLandlords: DropdownItem[];
  availableCrops: DropdownItem[];
  availableFields: DropdownItem[];
  harvestSpreadsheet: any;
  editableColumns: EditableColumnMetadata[];
  loading: boolean;
  updating: boolean;
  loadingDropdowns: boolean;
  error: string | null;
  updateError: string | null;
}

const initialState: HarvestDetailState = {
  availableYears: [],
  availableLandlords: [],
  availableCrops: [],
  availableFields: [],
  harvestSpreadsheet: {},
  editableColumns: [],
  loading: false,
  updating: false,
  loadingDropdowns: false,
  error: null,
  updateError: null,
};

export const fetchHarvestDetailDropdown = createAsyncThunk(
  "harvestdetail/dropdown/fetch",
  async (dropdownType: string) => {
    const response = await fetchHarvestDetailDropdownData(dropdownType);
    return response.data;
  }
);

export const fetchHarvestFieldsDropdown = createAsyncThunk(
  "harvestdetail/fields",
  async (landlordIds: number[]) => {
    const response = await fetchHarvestFieldsByLandlords(landlordIds);
    return response.data;
  }
);

export const fetchFilteredHarvestDetails = createAsyncThunk(
  "harvestdetail/filter",
  async (value: GetHarvestDetailsByYearAndLandlordsRequest) => {
    const response = await filterHarvestDetailData(value);
    return response.data;
  }
);

export const postUpdateHarvestDetail = createAsyncThunk(
  "harvestdetail/updateHarvestDetail",
  async (data: UpdateHarvestDetailRequest, { rejectWithValue }) => {
    try {
      const response = await putHarvestDetailApi(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const harvestDetailSlice = createSlice({
  name: "harvestdetail",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.updateError = null;
    },
    clearSpreadsheetData: (state) => {
      state.harvestSpreadsheet = {};
      state.editableColumns = [];
    },
    updateCellValueOptimistically: (state, action) => {
      const { rowIndex, columnIndex, value } = action.payload;
      if (state.harvestSpreadsheet?.sheets?.[0]?.rows?.[rowIndex]?.cells) {
        const cell = state.harvestSpreadsheet.sheets[0].rows[
          rowIndex
        ].cells.find((c: any) => c.index === columnIndex);
        if (cell) {
          cell.value = value;
        }
      }
    },
    updateCalculatedFields: (state, action) => {
      const calculatedFields: CalculatedFieldResponse[] = action.payload;

      calculatedFields.forEach((field) => {
        if (field.success) {
          const sheet = state.harvestSpreadsheet?.sheets?.[0];
          if (sheet?.rows) {
            for (const row of sheet.rows) {
              if (row.cells) {
                const cell = row.cells.find(
                  (c: any) =>
                    c.harvestDetailId === field.harvestDetailId &&
                    c.fieldName === field.fieldName
                );
                if (cell) {
                  cell.value = field.newValue;
                  break;
                }
              }
            }
          }
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch dropdown data
      .addCase(fetchHarvestDetailDropdown.pending, (state) => {
        state.loadingDropdowns = true;
        state.error = null;
      })
      .addCase(fetchHarvestDetailDropdown.fulfilled, (state, action) => {
        state.loadingDropdowns = false;
        if (action.meta.arg === "availableYears") {
          state.availableYears = action.payload.years || action.payload;
        } else if (action.meta.arg === "availableLandlords") {
          state.availableLandlords = action.payload.landlords || action.payload;
        } else if (action.meta.arg === "availableCrops") {
          state.availableCrops = action.payload.crops || action.payload;
        } else if (action.meta.arg === "availableFields") {
          state.availableFields = action.payload.fields || action.payload;
        }
      })
      .addCase(fetchHarvestFieldsDropdown.fulfilled, (state, action) => {
        state.availableFields = action.payload.fields || action.payload;
      })
      .addCase(fetchHarvestFieldsDropdown.rejected, (state) => {
        state.availableFields = [];
      })
      .addCase(fetchHarvestDetailDropdown.rejected, (state) => {
        state.loadingDropdowns = false;
        state.error = "Failed to fetch dropdown data.";
      })
      // Fetch filtered harvest details
      .addCase(fetchFilteredHarvestDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFilteredHarvestDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.harvestSpreadsheet = action.payload;
        state.editableColumns = action.payload.editableColumns || [];
      })
      .addCase(fetchFilteredHarvestDetails.rejected, (state) => {
        state.loading = false;
        state.error = "Failed to fetch harvest detail data.";
      })

      // Update harvest detail
      .addCase(postUpdateHarvestDetail.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(postUpdateHarvestDetail.fulfilled, (state, action) => {
        state.updating = false;
        const response: UpdateHarvestDetailResponse = action.payload;

        if (response.updatedCells) {
          response.updatedCells.forEach((updatedCell) => {
            if (updatedCell.success) {
              console.log(
                `Cell updated successfully: ${updatedCell.fieldName} = ${updatedCell.newValue}`
              );
            } else {
              console.error(
                `Failed to update cell: ${updatedCell.errorMessage}`
              );
            }
          });
        }

        if (response.calculatedFields) {
          harvestDetailSlice.caseReducers.updateCalculatedFields(state, {
            payload: response.calculatedFields,
            type: "updateCalculatedFields",
          });
        }

        if (response.errors && response.errors.length > 0) {
          state.updateError = response.errors.join("; ");
          toast.error(state.updateError);
        } else if (response.success) {
          toast.success("Harvest Detail saved successfully!");
        }
      })
      .addCase(postUpdateHarvestDetail.rejected, (state, action) => {
        state.updating = false;
        state.updateError =
          (action.payload as string) || "Failed to update harvest detail";
        toast.error(state.updateError);
      });
  },
});

export const {
  clearError,
  clearSpreadsheetData,
  updateCellValueOptimistically,
  updateCalculatedFields
} = harvestDetailSlice.actions;

// Selectors
export const selectSpreadsheetData = (state: { harvestdetail: HarvestDetailState }) => 
  state.harvestdetail.harvestSpreadsheet;
export const selectEditableColumns = (state: { harvestdetail: HarvestDetailState }) => 
  state.harvestdetail.editableColumns;
export const selectAvailableYears = (state: { harvestdetail: HarvestDetailState }) => 
  state.harvestdetail.availableYears;
export const selectAvailableLandlords = (state: { harvestdetail: HarvestDetailState }) => 
  state.harvestdetail.availableLandlords;
export const selectAvailableCrops = (state: { harvestdetail: HarvestDetailState }) => 
  state.harvestdetail.availableCrops;
export const selectAvailableFields = (state: { harvestdetail: HarvestDetailState }) => 
  state.harvestdetail.availableFields;
export const selectLoading = (state: { harvestdetail: HarvestDetailState }) => 
  state.harvestdetail.loading;
export const selectUpdating = (state: { harvestdetail: HarvestDetailState }) => 
  state.harvestdetail.updating;
export const selectLoadingDropdowns = (state: { harvestdetail: HarvestDetailState }) => 
  state.harvestdetail.loadingDropdowns;
export const selectError = (state: { harvestdetail: HarvestDetailState }) => 
  state.harvestdetail.error;
export const selectUpdateError = (state: { harvestdetail: HarvestDetailState }) => 
  state.harvestdetail.updateError;

export const selectColumnMetadata = (state: { harvestdetail: HarvestDetailState }) => 
  (columnIndex: number) => {
    return state.harvestdetail.editableColumns.find(col => col.columnIndex === columnIndex);
  };

export default harvestDetailSlice.reducer;